import React, { createContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useRequiredContext } from '../hooks';
import { EASYCOM_ROUTES } from '../navigation';
import { zAppInit, zAppInitParams } from './zod';
import { zSafeParse } from './zod/helpers';

type tContext = z.infer<typeof zAppInit>;

const AppInitContext = createContext<tContext>(undefined);

const isTrueSet = (input: string) => input === 'true';

export const AppInitProvider = ({ children }) => {
  const [initData, setInitData] = useState<tContext['params']>(undefined);
  const navigate = useNavigate();
  const refNavigate = useRef(navigate);

  useEffect(() => {
    const params =
      process.env.NODE_ENV === 'development'
        ? // eslint-disable-next-line no-restricted-globals
          new URLSearchParams(location.search)
        : // eslint-disable-next-line no-restricted-globals
          new URLSearchParams(location.search);

    const encodedRedirectUrl = params.get('redirectUrl');
    const redirectUrl = encodedRedirectUrl ? decodeURIComponent(encodedRedirectUrl) : null;

    const languageCode = params.get('languageCode') ?? '';
    const allowOrderFiltering = params.get('allowOrderFiltering');
    const allowOrderRowFiltering = params.get('allowOrderRowFiltering');
    const returnType = params.get('returnType');
    const drToken = params.get('drToken') ?? '';
    const email = params.get('email');
    const customerNumber = params.get('customerNumber');
    const loginType = params.get('loginType');

    const initOrderKeys : string[] = [];
    const initOrderLines : string[] = [];

    const orders: (string | null)[] = params.getAll('orders');
    orders.forEach((o) => {
      const split = o?.split('__');
      if (split) {
        if (!initOrderKeys.includes(split[0])) {
          initOrderKeys.push(split[0]);
        }
        if (split.length > 1) {
          if (!initOrderLines.includes(split[1])) {
            initOrderLines.push(split[1]);
          }
        }
      }
    });

    const appInitObject: tContext['params'] = {
      drToken,
      email,
      customerNumber,
      initOrderKeys,
      initOrderLines,
      loginType,
      redirectUrl,
      languageCode,
      allowOrderFiltering: isTrueSet(allowOrderFiltering ?? 'false'),
      allowOrderRowFiltering: isTrueSet(allowOrderRowFiltering ?? 'true'),
      returnType,
    };

    const validAppInitObject = zSafeParse(zAppInitParams, appInitObject);
    setInitData(validAppInitObject);

    if (loginType === '0') {
      if (!email || initOrderKeys.length === 0) {
        refNavigate.current(EASYCOM_ROUTES.LOGIN);
      }
    }

    if (loginType === '1') {
      if (!customerNumber || initOrderKeys.length === 0) {
        refNavigate.current(EASYCOM_ROUTES.LOGIN);
      }
    }

    if (initOrderKeys.length > 0 && (customerNumber || email)) {
      refNavigate.current(EASYCOM_ROUTES.ORDER_PAGE);
    }
  }, []);

  const appData = {
    params: initData,
    updateDefaultParams: setInitData,
  };

  AppInitContext.displayName = 'AppInitProvider';

  if (appData.params) {
    return (
      <AppInitContext.Provider value={appData}>
        {initData && children}
      </AppInitContext.Provider>
    );
  }
  return null;
};

export const useAppInit = () => useRequiredContext(AppInitContext);
